import { MESSAGES } from '@aibees/elephant/constants';
import { AddListToInputCheckbox } from '@aibees/elephant/feature/search-table-people/components/add-list-to-input-checkbox';
import { useBootstrapLabels } from '@aibees/elephant/hooks/bootstrap/use-bootstrap-labels';
import { useFilterLabelsByOwnershipTab } from '@aibees/elephant/hooks/labels/use-label-ownership-tabs';
import { useLabelTypeFromRoute } from '@aibees/elephant/hooks/labels/use-label-type-from-route';
import { Label } from '@aibees/elephant/types/label';
import { CardBox, Grid2, Icon, Scrollbar } from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { classnames, searchArrayOfObjects } from '@aibees/shared/utils';
import { TabContext, TabList } from '@mui/lab';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Stack,
  Tab,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { sortBy } from 'lodash-es';
import React, { memo, useEffect, useState } from 'react';
import { useLabelManagementContext } from '../search-table-action-dialog-lists/context';
import * as Styled from './styles';
import * as SX from './sx';
import { type RecordLabelManagerProps as Props } from './types';

// All / Mine / Team. PresetVisibility is the legacy carrier for the tab value;
// what it selects is the creator, not the item's visibility.
const OWNERSHIP_TABS = [
  PresetVisibility.ALL,
  PresetVisibility.PRIVATE,
  PresetVisibility.INTERNAL,
];

export const RecordLabelManager = memo(
  ({
    className,
    title,
    type,
    state,
    currentLabels,
    loading,
    onSubmit,
    onClose,
  }: Props) => {
    const { labels, justCreatedLabels } = useLabelManagementContext();
    const labelType = useLabelTypeFromRoute();

    const [tabValue, setTabValue] = useState(PresetVisibility.ALL);
    const [checkedItems, setCheckedItems] =
      useState<Label[]>(justCreatedLabels);
    const [uncheckedItems, setUncheckedItems] = useState<Label[]>([]);
    const [searchText, setSearchText] = useState('');

    const relevantCheckedItems = checkedItems.filter(
      (item) => item.type === type
    ); // TODO is it needed to check the label type?
    const disabled = relevantCheckedItems.length === 0;
    const data = useBootstrapLabels(type, labelType);
    const filterByOwnershipTab = useFilterLabelsByOwnershipTab();
    const tabItems = filterByOwnershipTab(data.items, tabValue);
    const searchResults = searchArrayOfObjects(
      tabItems as never[],
      'name',
      searchText
    );

    const items = sortBy(
      searchText ? searchResults : tabItems,
      (item) => checkedItems.map((x) => x.id).indexOf(item.id) < 0
    ) as Label[];

    const handleChange = (checked: boolean, label: Label) => {
      setCheckedItems((prevChecked) =>
        checked
          ? [label, ...prevChecked]
          : prevChecked.filter((i) => i.id !== label.id)
      );

      setUncheckedItems((prevUnchecked) =>
        checked
          ? prevUnchecked.filter((i) => i.id !== label.id)
          : [...prevUnchecked, label]
      );
    };

    const handleTabChange = (
      _: React.SyntheticEvent,
      newValue: PresetVisibility
    ) => setTabValue(newValue);

    const handleSearch: TextFieldProps['onChange'] = (e) => {
      setSearchText(e.target.value);
    };

    const handleSubmit: ButtonProps['onClick'] = () => {
      onSubmit?.(checkedItems, uncheckedItems);
    };

    const handleClearAll = () => setCheckedItems([]);

    const handleSelectAll = () => setCheckedItems(items);

    useEffect(() => {
      setCheckedItems([
        ...(labels ?? []),
        ...checkedItems,
        ...(currentLabels ?? []),
        ...justCreatedLabels,
      ]);
    }, [labels, justCreatedLabels]);

    return (
      <Styled.Wrapper
        className={classnames('RecordLabelManager', className)}
        sx={SX.root}
        contentSx={SX.content}
      >
        {title && <Typography sx={SX.title}>{title}</Typography>}

        <Styled.Input
          name="name"
          placeholder="Search List"
          size="small"
          InputProps={{
            startAdornment: (
              <Icon name="search-fill" sxx={SX.searchIcon} size={14} />
            ),
          }}
          value={searchText}
          onChange={handleSearch}
          autoComplete="off"
        />

        <CardBox contentSx={SX.cardBox}>
          <TabContext value={tabValue}>
            <TabList onChange={handleTabChange} variant="fullWidth">
              {OWNERSHIP_TABS.map((tab) => (
                <Tab
                  key={tab}
                  label={
                    <Typography sx={SX.tabTitle}>{MESSAGES[tab]}</Typography>
                  }
                  value={tab}
                />
              ))}
            </TabList>
          </TabContext>
          <Scrollbar sx={{ height: (p) => p.spacing(20) }}>
            {items?.map((i) => (
              <AddListToInputCheckbox
                key={i.id}
                item={i}
                items={items}
                checked={checkedItems.some(
                  (checkedItem) => checkedItem.id === i.id
                )}
                disabled={currentLabels?.some(
                  (checkedItem) => checkedItem.id === i.id
                )}
                onChange={handleChange}
              />
            ))}
          </Scrollbar>
        </CardBox>
        <Stack direction="row" justifyContent="space-between">
          <Grid2>
            <Button
              sx={SX.cancelButton}
              variant="text"
              onClick={handleClearAll}
              disabled={!checkedItems.length}
            >
              Clear All
            </Button>
            <Button
              sx={SX.cancelButton}
              variant="text"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
          </Grid2>

          <Grid2>
            <Button variant="text" sx={SX.cancelButton} onClick={onClose}>
              Cancel
            </Button>

            <Button
              sx={SX.applyButton}
              color="primary"
              disabled={disabled || loading || data.isLoading}
              onClick={handleSubmit}
            >
              {loading || data.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Apply'
              )}
            </Button>
          </Grid2>
        </Stack>
      </Styled.Wrapper>
    );
  }
);
RecordLabelManager.displayName = 'RecordLabelManager';
